import { logError } from '@/utilities/log'
import {
  addLineItemsHandler,
  clearCartHandler,
  getCartHandler,
  updateCartHandler,
  updateLineItemHandler
} from '../cart-api'
import { calculateCartTotals } from '../optimistic-updates/calculate-cart-totals'
import { optimisticAddLineItems } from '../optimistic-updates/optimistic-add-line-items'
import { optimisticUpdateCart } from '../optimistic-updates/optimistic-update-cart'
import { optimisticUpdateLineItem } from '../optimistic-updates/optimistic-update-line-item'
import { Cart, CartLineItem, LineItemInput, UpdateCartInput, UpdateLineItemInput } from '../types'

type CartApiClient = {
  addLineItems: typeof addLineItemsHandler
  clearCart: typeof clearCartHandler
  getCart: typeof getCartHandler
  updateCart: typeof updateCartHandler
  updateLineItem: typeof updateLineItemHandler
}

export type CartResult =
  | {
      cart: Cart
      error: null
    }
  | {
      cart: Cart | null
      error: string
    }

type CartOperation =
  | {
      name: 'load'
    }
  | {
      name: 'clear'
    }
  | {
      name: 'update'
      input: UpdateCartInput
    }
  | {
      name: 'addLineItems'
      input: LineItemInput[]
    }
  | {
      name: 'updateLineItem'
      input: UpdateLineItemInput
    }

type CartOperationResolver = CartOperation & {
  resolve: (result: CartResult) => void
}

const emptyCart: Cart = {
  attributes: {},
  cart_level_discount_applications: [],
  currency: 'USD',
  item_count: 0,
  items: [],
  items_subtotal_price: 0,
  note: null,
  original_total_price: 0,
  requires_shipping: false,
  total_discount: 0,
  total_price: 0,
  total_weight: 0
}

export default class CartStore extends EventTarget {
  private _cart: Cart | null = null
  public error: string | null = null
  private _rootRoute = '/'

  private operations: CartOperationResolver[] = []
  private pendingOperation: CartOperationResolver | null = null

  public constructor(private readonly client: CartApiClient) {
    super()
  }

  public get rootRoute(): string {
    return this._rootRoute
  }

  public set rootRoute(route: string) {
    this._rootRoute = route.endsWith('/') ? route : `${route}/`
  }

  public get cart(): Cart | null {
    let cart = this._cart
    for (const operation of [this.pendingOperation, ...this.operations]) {
      switch (operation?.name) {
        case 'clear':
          cart = emptyCart
          break
        case 'addLineItems':
          if (cart) cart = optimisticAddLineItems(operation.input, cart)
          break
        case 'updateLineItem':
          if (cart) cart = optimisticUpdateLineItem(operation.input, cart)
          break
        case 'update':
          if (cart) cart = optimisticUpdateCart(operation.input, cart)
          break
      }
    }

    return cart
  }

  private set cart(cart: Cart | null) {
    this._cart = cart
  }

  public async addLineItems(input: LineItemInput[]): Promise<CartResult> {
    return this.enqueueOperation({ name: 'addLineItems', input })
  }

  public checkout(overrideUrl?: string): void {
    window.location.href = overrideUrl ?? '/checkout'
  }

  public async clearCart(): Promise<CartResult> {
    return this.enqueueOperation({ name: 'clear' })
  }

  public async loadCart(): Promise<CartResult> {
    return this.enqueueOperation({ name: 'load' })
  }

  public async updateCart(input: UpdateCartInput): Promise<CartResult> {
    return this.enqueueOperation({ name: 'update', input })
  }

  public async updateLineItem(input: UpdateLineItemInput): Promise<CartResult> {
    return this.enqueueOperation({ name: 'updateLineItem', input })
  }

  public waitForSettle(): Promise<void> {
    const isSettled = !this.pendingOperation && this.operations.length === 0
    return isSettled
      ? Promise.resolve()
      : new Promise(res => this.addEventListener('cartSettled', () => res(), { once: true }))
  }

  private enqueueOperation(operation: CartOperation): Promise<CartResult> {
    return new Promise<CartResult>(resolve => {
      this.operations.push({ ...operation, resolve })
      this.operations = reduceOperations(this.operations)
      // Optimistic update is triggered synchronously,
      // then the operation is resolved asynchronously.
      // This allows us to update the cart state immediately,
      // while the actual API call is being processed.
      this.dispatchEvent(
        new CustomEvent('cartUpdated', {
          detail: {
            cart: this.cart,
            error: this.error
          }
        })
      )
      // Start processing the next operation if none
      void this.nextOperation()
    })
  }

  private async nextOperation() {
    if (this.pendingOperation) {
      return
    }
    if (this.operations.length === 0) {
      this.dispatchEvent(new CustomEvent('cartSettled'))
      return
    }

    const operation = this.operations.shift()!
    await this.resolveOperation(operation)
    await this.nextOperation()
  }

  private async resolveOperation(operation: CartOperationResolver) {
    try {
      this.pendingOperation = operation
      let cart: Cart

      switch (operation.name) {
        case 'load':
          cart = await this.client.getCart(this.rootRoute)
          break
        case 'clear':
          cart = await this.client.clearCart(this.rootRoute)
          break
        case 'update':
          const updatedCart = await this.client.updateCart(operation.input, this.rootRoute)
          cart = { ...updatedCart, items: updatedCart.items.filter(item => item.quantity > 0) }
          break
        case 'addLineItems':
          await this.client.addLineItems(operation.input, this.rootRoute)
          cart = await this.client.getCart(this.rootRoute)
          break
        case 'updateLineItem':
          cart = await this.client.updateLineItem(operation.input, this.rootRoute)
          break
      }

      this.pendingOperation = null
      // Resolve the operation with the updated
      operation.resolve(this.succeed(cart))
    } catch (error) {
      this.pendingOperation = null
      operation.resolve(
        await this.fail(
          errorMessage(error, `Cart Store failed to ${operation.name}`),
          operation.name !== 'load'
        )
      )
    }
  }

  private succeed(cart: Cart): CartResult {
    this.cart = cart
    this.error = null
    const result = { cart: this.cart, error: this.error }
    this.dispatchEvent(new CustomEvent('cartUpdated', { detail: result }))
    return result
  }

  private async fail(error: string, loadCart = true): Promise<CartResult> {
    logError(error, { message: 'CartStoreError' })

    if (loadCart) {
      try {
        this._cart ??= await this.client.getCart()
      } catch (e) {
        logError(e, { message: 'ErrorLoadingCartOnFailure' })
      }
    }

    const result = { cart: this._cart, error: (this.error = error) }
    this.dispatchEvent(new CustomEvent('cartError', { detail: result }))
    return result
  }
}

/**
 * Extracts the error message from an error object.
 * If the error is an instance of Error, its message will be returned.
 * Otherwise, a default message will be returned.
 *
 * @param error - The error to extract the message from.
 * @param defaultMessage - The default message to return if the error is not an instance of Error.
 * @returns The extracted error message or the default message.
 */
function errorMessage(error: unknown, defaultMessage = 'Something went wrong.'): string {
  return error instanceof Error ? error.message : defaultMessage
}

/**
 * Reduces a list of cart operations by merging similar operations.
 * This is an optimization for slow networks, in cases when multiple
 * operations are queued in a short time frame.
 * When operations are merged, each one resolves with the combined result.
 *
 * @param operations - The list of cart operations to reduce.
 * @returns The reduced list of cart operations.
 */
function reduceOperations(operations: CartOperationResolver[]): CartOperationResolver[] {
  if (operations.length < 2) {
    return operations
  }

  return operations.reduce<CartOperationResolver[]>((ops, current) => {
    // Deduplicate redundant 'load' operations
    const loaders: ((result: CartResult) => void)[] = []

    if (!isAdd(current)) {
      while (isLoad(ops[ops.length - 1])) {
        loaders.push(ops.pop()!.resolve)
      }
    }

    // The resolve functions of all the operations are combined,
    // to ensure they will all resolve at the same time, with the same result.
    if (loaders.length > 0) {
      current.resolve = tee(current.resolve, ...loaders)
    }

    const lastIndex = ops.length - 1
    const prev = ops[lastIndex]
    const resolve = tee(current.resolve, prev?.resolve)

    // Combine 'addLineItems' operations
    if (isAdd(current) && isAdd(prev)) {
      ops[lastIndex] = {
        ...prev,
        input: prev.input.concat(current.input),
        resolve
      }
      return ops
    }

    // Combine 'quantity-only' line updates into a single cart update
    if (isQtyOnly(current) && isQtyOnly(prev) && !isSameItem(prev.input, current.input)) {
      ops[lastIndex] = {
        ...prev,
        name: 'update',
        input: {
          updates: {
            [current.input.id.toString()]: current.input.quantity,
            [prev.input.id.toString()]: prev.input.quantity
          }
        },
        resolve
      }
      return ops
    }

    // Accumulate multiple 'quantity-only' line updates into cart update
    if (isQtyOnly(current) && isUpdate(prev)) {
      ops[lastIndex] = {
        ...prev,
        input: {
          ...prev.input,
          updates: {
            ...prev.input.updates,
            [current.input.id.toString()]: current.input.quantity
          }
        },
        resolve
      }
      return ops
    }

    // Combine 'updateLineItem' operations for the same item
    if (isChange(current) && isChange(prev) && isSameItem(prev.input, current.input)) {
      const input = {
        ...prev.input,
        ...current.input
      } as UpdateLineItemInput

      const properties = {
        ...prev.input.properties,
        ...current.input.properties
      }

      if (Object.keys(properties).length > 0) {
        input.properties = properties
      }

      prev.input = input
      ops[lastIndex] = {
        ...prev,
        input,
        resolve
      }
      return ops
    }

    // Complete any other operations normally
    ops.push(current)
    return ops
  }, [])
}

/**
 * Creates a function that calls multiple functions with the same value.
 * This allows us to combine multiple callbacks into a single function
 * that can be used in places where a single callback is expected.
 *
 * @param fns - A list of functions to call with the same value.
 * Each function should accept a single argument of type T.
 * @returns A function that accepts a single argument of type T and calls all
 * functions in the list with that argument.
 */
function tee<T>(...fns: ((value: T) => void)[]): (value: T) => void {
  return (value: T) => {
    for (const fn of fns) {
      fn(value)
    }
  }
}

/**
 * This is a type guard that narrows the type of the operation to the specified name.
 *
 * @param name - The name of the operation to check for.
 * @returns A function that checks if the operation has the specified name.
 */
function hasName<T extends CartOperation['name']>(name: T) {
  return (op?: CartOperationResolver): op is { name: T } & CartOperationResolver =>
    op?.name === name
}

const isAdd = hasName('addLineItems')
const isChange = hasName('updateLineItem')
const isLoad = hasName('load')
const isUpdate = hasName('update')

/**
 * If an updateLineItem operation only changes the quantity of a line item,
 * it is considered a "quantity-only" update.
 * This means that the operation does not change the selling plan or properties
 * of the line item, and only updates the quantity.
 *
 * @param op - The operation to check.
 * @returns A boolean indicating whether the operation is a quantity-only update.
 */
function isQtyOnly(op: CartOperationResolver): op is {
  name: 'updateLineItem'
  input: UpdateLineItemInput & { id: string; quantity: number }
} & CartOperationResolver {
  return (
    isChange(op) &&
    typeof op.input.id === 'string' &&
    typeof op.input.quantity === 'number' &&
    !op.input.selling_plan &&
    !op.input.properties
  )
}

function isSameItem(a: UpdateLineItemInput, b: UpdateLineItemInput): boolean {
  return a.id === b.id && a.line === b.line
}

/**
 * Merges line items from the current cart and the initial cart.
 * This allows us to update the cart state after an `addLineItems` operation
 * without having to reload the entire cart.
 *
 * @param items - The line items to merge.
 * @param initialCart - The initial cart state.
 * @returns The updated cart state.
 */
export function mergeLineItems(items: CartLineItem[], initialCart: Cart): Cart {
  const newItems: CartLineItem[] = []
  const initialItems = new Map((initialCart.items || []).map(item => [uniqueItemKey(item), item]))
  for (const item of items) {
    const key = uniqueItemKey(item)
    // Items already in cart are updated in place. New items are added to the top of the list.
    // This is consistent with how Shopify handles cart 'add' operations.
    initialItems.has(key) ? initialItems.set(key, item) : newItems.push(item)
  }
  newItems.push(...initialItems.values())

  return calculateCartTotals(newItems, initialCart)
}

/**
 * Shopify creates separate line items for:
 * - different variant ids
 * - different selling plans
 * - different properties
 */
function uniqueItemKey(item: CartLineItem): string {
  return `${item.id}:${item.selling_plan_allocation?.selling_plan.id}${JSON.stringify(item.properties)}`
}
